import {
  CENTRAL_DOMAINS_MODEL,
  assertDomainDefinition,
  buildDomainCapabilities,
  buildDomainConfigurationPolicy,
  buildDomainSurfaces,
  capabilityActivationPolicy,
  deriveDomainConfiguration,
  resolveEffectiveDomain,
} from './centralModel.js';
import { DOMAIN_CATALOG } from './catalog.js';
import {
  CENTRAL_GOVERNED_DOMAIN_CONFIGS,
  PROTECTED_DOMAIN_DEFINITIONS,
  SCHOOL_SETTABLE_GOVERNED_PATHS,
} from './protectedDefinitions.js';

export const GENERIC_RECORDING_PROTOTYPE_ID = 'swan.generic-recording';

const IMMUTABLE_DOMAIN_BINDINGS = Object.freeze([
  'name',
  'collections',
  'eventsCollection',
  'rulesCollection',
  'actionsCollection',
  'ruleFiresCollection',
]);

function clone(value) {
  if (Array.isArray(value)) return value.map(clone);
  if (!value || typeof value !== 'object') return value;
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [key, clone(child)]));
}

function getPath(source, path) {
  return path.split('.').reduce((value, part) => value?.[part], source);
}

function setPath(target, path, value) {
  const parts = path.split('.');
  let cursor = target;
  parts.forEach((part, index) => {
    if (index === parts.length - 1) cursor[part] = clone(value);
    else {
      cursor[part] ||= {};
      cursor = cursor[part];
    }
  });
}

function pickSchoolOverrides(source) {
  const output = {};
  for (const path of SCHOOL_SETTABLE_GOVERNED_PATHS) {
    const value = getPath(source, path);
    if (value !== undefined) setPath(output, path, value);
  }
  return output;
}

export function deriveDomainCollections(name) {
  const safeName = String(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return {
    events: `domain_${safeName}_events`,
    types: `domain_${safeName}_types`,
    categories: `domain_${safeName}_categories`,
    actions: `domain_${safeName}_actions`,
    rules: `domain_${safeName}_rules`,
    queue: `domain_${safeName}_queue`,
  };
}

function catalogConfig(entry) {
  const spec = entry.spec;
  const collections = deriveDomainCollections(spec.name);
  return {
    ...clone(spec),
    protected: false,
    active: true,
    sortOrder: spec.sortOrder ?? 10,
    defaultInputMethod: spec.defaultInputMethod || 'list',
    inputSurfaces: ['quick-issue'],
    collections,
    eventsCollection: collections.events,
    rulesCollection: collections.rules,
    actionDefinitions: [],
    parentMessages: { version: 1, defaultId: null, definitions: [] },
    capabilities: buildDomainCapabilities({
      rules: { enabled: false, categories: ['mention', 'threshold', 'escalation'] },
      actions: { enabled: false, outcomes: ['queue-follow-up'] },
      messages: { enabled: false, delivery: 'automatic-event' },
      collective: {
        enabled: true,
        aggregates: ['count', 'breakdown', 'trend', 'baseline'],
        audiences: ['staff', 'parent-summary'],
      },
    }),
    surfaces: buildDomainSurfaces({
      rules: false,
      dashboard: true,
      reports: true,
    }),
    reporting: {
      parentWeeklySummary: false,
      staffWeeklyBrief: false,
    },
    contextTags: spec.contextTags || {},
    scope: spec.scope || { grades: [] },
  };
}

function supportedSchoolCapabilities(config) {
  return ['register', 'rules', 'actions', 'messages', 'currency', 'collective', 'stories']
    .filter((key) => {
      const declaration = config.capabilities?.[key];
      if (declaration?.enabled === true) return true;
      return ['categories', 'mechanisms', 'outcomes', 'aggregates', 'delivery', 'schoolFields']
        .some((detail) => declaration?.[detail] !== undefined);
    });
}

function standardConfigurationPolicy(config, overrides = {}) {
  const schoolConfigurable = supportedSchoolCapabilities(config);
  const recording = config.capabilities?.recording;
  const recordingEnabled = typeof recording === 'boolean'
    ? recording
    : recording?.enabled === true;
  const policyOverrides = {
    capabilities: capabilityActivationPolicy(config.capabilities, schoolConfigurable),
    event: recordingEnabled
      ? {}
      : {
          adapter: config.capabilities?.policyLibrary?.enabled === true
            ? 'policy-library'
            : 'unsupported',
          operations: [],
          fieldTypes: [],
        },
    rules: {
      mechanisms: clone(
        config.capabilities?.rules?.mechanisms
        || config.capabilities?.rules?.categories
        || [],
      ),
      operations: schoolConfigurable.includes('rules')
        ? ['add', 'edit', 'retire', 'reorder']
        : [],
    },
    actions: {
      outcomes: clone((config.capabilities?.actions?.outcomes || []).map((outcome) => (
        outcome === 'follow-up' ? 'queue-follow-up' : outcome
      ))),
      operations: schoolConfigurable.includes('actions')
        ? ['add', 'edit', 'retire', 'reorder']
        : [],
    },
    reports: {
      operations: config.capabilities?.collective?.enabled === true ? ['edit'] : [],
    },
    ...overrides,
  };
  return buildDomainConfigurationPolicy(policyOverrides);
}

function runtimeFor(name, overrides = {}) {
  const knownAdapters = new Set([
    'attendance',
    'daily-register',
    'period-register',
    'participation',
    'incident-reports',
    'health',
    'academics',
    'policies',
  ]);
  return Object.freeze({
    adapter: knownAdapters.has(name) ? name : 'generic',
    alwaysOn: false,
    protected: false,
    immutableBindings: [...IMMUTABLE_DOMAIN_BINDINGS],
    ...clone(overrides),
  });
}

function definitionFor(name, config, options = {}) {
  const id = options.id || `swan.${name}`;
  return Object.freeze({
    id,
    modelId: CENTRAL_DOMAINS_MODEL.id,
    modelVersion: CENTRAL_DOMAINS_MODEL.version,
    definitionVersion: options.definitionVersion || 3,
    prototypeId: options.prototypeId || id,
    prototypeVersion: options.prototypeVersion || 1,
    runtime: runtimeFor(name, options.runtime),
    configurationPolicy: options.configurationPolicy
      || standardConfigurationPolicy(config),
    config: Object.freeze({
      reporting: {
        parentWeeklySummary: false,
        staffWeeklyBrief: false,
      },
      ...clone(config),
      name,
    }),
    overridePolicy: Object.freeze({ school: SCHOOL_SETTABLE_GOVERNED_PATHS }),
  });
}

const catalogConfigs = Object.fromEntries(
  DOMAIN_CATALOG
    .filter((entry) => entry.kind === 'governed' && entry.availability === 'launch' && entry.spec)
    .map((entry) => [entry.id, catalogConfig(entry)]),
);

function protectedPrototypeConfig(id) {
  const source = PROTECTED_DOMAIN_DEFINITIONS[id];
  const isBehaviour = id === 'discipline';
  return {
    ...clone(source),
    label: isBehaviour ? 'Behaviour' : source.label,
    actionDefinitions: [],
    parentMessages: { version: 1, defaultId: null, definitions: [] },
    capabilities: buildDomainCapabilities({
      rules: {
        enabled: true,
        categories: isBehaviour
          ? ['mention', 'threshold', 'escalation', 'zero-tolerance', 'accountability', 'contract']
          : ['mention', 'threshold', 'escalation', 'zero-tolerance', 'contract'],
      },
      actions: {
        enabled: true,
        outcomes: isBehaviour ? ['consequence'] : ['sticker', 'award'],
      },
      messages: { enabled: true, delivery: 'established-runtime' },
      collective: {
        enabled: true,
        aggregates: ['count', 'breakdown', 'trend', 'baseline'],
        audiences: ['staff', 'parent-summary'],
      },
      currency: !isBehaviour,
      stories: true,
    }),
    surfaces: buildDomainSurfaces({
      rules: true,
      dashboard: true,
      reports: true,
    }),
    reporting: {
      parentWeeklySummary: false,
      staffWeeklyBrief: false,
    },
  };
}

const genericRecordingPrototypeConfig = Object.freeze({
  name: 'generic-recording',
  label: 'Custom Domain',
  description: 'A school-configured Domain using Swan’s shared recording model.',
  protected: false,
  active: true,
  sortOrder: 10,
  icon: null,
  color: 'primary',
  defaultInputMethod: 'list',
  inputSurfaces: ['quick-issue'],
  primary: null,
  followUp: [],
  actionDefinitions: [],
  parentMessages: { version: 1, defaultId: null, definitions: [] },
  collections: {},
  capabilities: buildDomainCapabilities({
    recording: {
      enabled: true,
      context: 'event',
      supportedContexts: ['event', 'assessment'],
      schoolFields: ['context'],
    },
    register: {
      enabled: false,
      statusFieldId: null,
      implicitValue: null,
      statusFieldTypes: ['multiChoice'],
      schoolFields: ['statusFieldId', 'implicitValue'],
    },
    rules: { enabled: false, categories: ['mention', 'threshold', 'escalation'] },
    actions: { enabled: false, outcomes: ['queue-follow-up'] },
    messages: { enabled: false, delivery: 'automatic-event' },
    currency: { enabled: false, schoolFields: [] },
    collective: {
      enabled: true,
      aggregates: ['count', 'breakdown', 'trend', 'baseline'],
      audiences: ['staff', 'parent-summary'],
    },
  }),
  surfaces: buildDomainSurfaces({ dashboard: true, reports: true }),
  reporting: { parentWeeklySummary: false, staffWeeklyBrief: false },
  terminology: {
    event: { label: 'event', plural: 'events', description: '' },
    category: { label: 'category', plural: 'categories', description: '' },
    type: { label: 'type', plural: 'types', description: '' },
    action: { label: 'action', plural: 'actions', description: '' },
    rule: { label: 'rule', plural: 'rules', description: '' },
    queue: { label: 'queue', plural: 'queues', description: '' },
  },
  contextTags: {},
  scope: { grades: [] },
});

export const CENTRAL_DOMAIN_CONFIGS = Object.freeze({
  discipline: protectedPrototypeConfig('discipline'),
  recognition: protectedPrototypeConfig('recognition'),
  ...CENTRAL_GOVERNED_DOMAIN_CONFIGS,
  ...catalogConfigs,
  'generic-recording': genericRecordingPrototypeConfig,
});

const protectedPolicies = {
  discipline: buildDomainConfigurationPolicy({
    overview: { paths: ['description', 'scope', 'phaseApplicability'] },
    event: {
      adapter: 'behaviour-events',
      pathPolicy: {
        'presentation.classDisplay.enabled': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Behaviour uses its established recording surfaces.',
        },
        'presentation.classDisplay.statusFieldId': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Behaviour uses its established recording surfaces.',
        },
        'presentation.inputSurfaces': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Behaviour uses its established recording surfaces.',
        },
        'presentation.quickIssue.enabled': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Behaviour Quick Issue availability is part of its protected runtime.',
        },
        'presentation.quickIssue.notes': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Behaviour Quick Issue fields are managed by its established editor.',
        },
        'presentation.quickIssue.attachments': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Behaviour Quick Issue fields are managed by its established editor.',
        },
      },
    },
    actions: { adapter: 'behaviour-actions', outcomes: ['consequence'] },
    rules: {
      adapter: 'behaviour-rules',
      mechanisms: ['mention', 'threshold', 'escalation', 'zero-tolerance', 'accountability', 'contract'],
    },
    reports: { adapter: 'behaviour-reports' },
    capabilities: capabilityActivationPolicy(
      protectedPrototypeConfig('discipline').capabilities,
    ),
  }),
  recognition: buildDomainConfigurationPolicy({
    overview: { paths: ['description', 'scope', 'phaseApplicability'] },
    event: {
      adapter: 'recognition-events',
      pathPolicy: {
        'presentation.classDisplay.enabled': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Recognition uses its established recording surfaces.',
        },
        'presentation.classDisplay.statusFieldId': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Recognition uses its established recording surfaces.',
        },
        'presentation.inputSurfaces': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Recognition uses its established recording surfaces.',
        },
        'presentation.quickIssue.enabled': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Recognition Quick Issue availability is part of its protected runtime.',
        },
        'presentation.quickIssue.notes': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Recognition Quick Issue fields are managed by its established editor.',
        },
        'presentation.quickIssue.attachments': {
          state: 'locked-with-reason',
          configurable: false,
          reason: 'Recognition Quick Issue fields are managed by its established editor.',
        },
      },
    },
    actions: { adapter: 'recognition-actions', outcomes: ['sticker', 'award'] },
    rules: {
      adapter: 'recognition-rules',
      mechanisms: ['mention', 'threshold', 'escalation', 'zero-tolerance', 'contract'],
    },
    reports: { adapter: 'recognition-reports' },
    capabilities: capabilityActivationPolicy(
      protectedPrototypeConfig('recognition').capabilities,
    ),
  }),
};

export const CENTRAL_DOMAIN_DEFINITIONS = Object.freeze(Object.fromEntries(
  Object.entries(CENTRAL_DOMAIN_CONFIGS).map(([name, config]) => {
    const isProtectedRuntime = name === 'discipline' || name === 'recognition';
    const isGeneric = name === 'generic-recording';
    const hasRecordingContextContract = isGeneric || name === 'academics';
    return [name, definitionFor(name, config, {
      id: isGeneric ? GENERIC_RECORDING_PROTOTYPE_ID : undefined,
      definitionVersion: hasRecordingContextContract ? 4 : undefined,
      prototypeVersion: hasRecordingContextContract ? 2 : undefined,
      configurationPolicy: isProtectedRuntime
        ? protectedPolicies[name]
        : standardConfigurationPolicy(config),
      runtime: isProtectedRuntime
        ? {
            adapter: name === 'discipline' ? 'behaviour' : 'recognition',
            // Recognition's existing repository reads mixed historical
            // timestamp shapes; server ordering would omit older records.
            eventOrderField: name === 'recognition' ? null : 'createdAt',
            alwaysOn: true,
            protected: true,
          }
        : undefined,
    })];
  }),
));

Object.values(CENTRAL_DOMAIN_DEFINITIONS).forEach(assertDomainDefinition);

const definitionsById = new Map(
  Object.values(CENTRAL_DOMAIN_DEFINITIONS).map((definition) => [definition.id, definition]),
);

export function getCentralDomainDefinition(idOrName) {
  if (!idOrName) return null;
  return CENTRAL_DOMAIN_DEFINITIONS[idOrName] || definitionsById.get(idOrName) || null;
}

function normalizeDefinitionCandidate(value) {
  if (typeof value !== 'string') return null;
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
  return normalized || null;
}

/**
 * Match a configured navigation item to a central Domain definition. This is
 * deliberately based on semantic identity (definition id, Domain id/name,
 * label, or a single-segment legacy route), never on one hard-coded Domain.
 */
export function centralDefinitionForMenuItem(item) {
  const href = typeof item?.href === 'string'
    ? item.href.split(/[?#]/, 1)[0]
    : null;
  const routeCandidate = href && /^\/[a-z0-9_-]+\/?$/i.test(href) ? href : null;
  const candidates = [
    item?.definitionId,
    item?.domainDefinitionId,
    item?.domainId,
    item?.id,
    item?.label,
    routeCandidate,
  ];

  for (const candidate of candidates) {
    const direct = getCentralDomainDefinition(candidate);
    if (direct) return direct;
    const normalized = normalizeDefinitionCandidate(candidate);
    const matched = getCentralDomainDefinition(normalized)
      || Object.values(CENTRAL_DOMAIN_DEFINITIONS).find((definition) =>
        normalizeDefinitionCandidate(definition.config?.name) === normalized);
    if (matched) return matched;
  }
  return null;
}

/**
 * Stored menu configuration may outlive a Domain's UI contract. A central
 * Domain can appear as its own destination only while the definition declares
 * an enabled standalone surface with the same route.
 */
export function centralDomainAllowsMenuItem(item) {
  const definition = centralDefinitionForMenuItem(item);
  if (!definition) return true;

  const standalone = definition.config?.surfaces?.standalone;
  if (standalone?.enabled !== true || !standalone.route) return false;

  const configuredRoute = typeof item?.href === 'string'
    ? item.href.split(/[?#]/, 1)[0].replace(/\/$/, '') || '/'
    : null;
  const centralRoute = String(standalone.route).replace(/\/$/, '') || '/';
  return configuredRoute === centralRoute;
}

export function filterMenuItemsByCentralDomainSurfaces(items) {
  if (!Array.isArray(items)) return [];
  return items
    .filter(centralDomainAllowsMenuItem)
    .map((item) => Array.isArray(item.children)
      ? { ...item, children: filterMenuItemsByCentralDomainSurfaces(item.children) }
      : item);
}

export function centralDefinitionForStoredDomain(domain) {
  return getCentralDomainDefinition(domain?.governance?.prototypeId)
    || getCentralDomainDefinition(domain?.governance?.definitionId)
    || getCentralDomainDefinition(domain?.name)
    || getCentralDomainDefinition(domain?.id)
    || null;
}

/**
 * Resolve the concrete Definition used by an installation. Centrally supplied
 * Domains use their Prototype Definition directly. A school-authored Domain
 * gets a stable school Definition id while inheriting the generic Prototype's
 * structure and configuration policy.
 */
export function domainDefinitionForStoredDomain(stored) {
  const matched = centralDefinitionForStoredDomain(stored);
  if (matched && matched.id !== GENERIC_RECORDING_PROTOTYPE_ID) return matched;

  const generic = getCentralDomainDefinition(GENERIC_RECORDING_PROTOTYPE_ID);
  const definitionId = stored?.governance?.definitionId
    && stored.governance.definitionId !== GENERIC_RECORDING_PROTOTYPE_ID
    ? stored.governance.definitionId
    : `school.${stored?.id || stored?.name || 'domain'}`;
  return {
    ...generic,
    id: definitionId,
    definitionVersion: Number.isInteger(stored?.governance?.definitionVersion)
      ? stored.governance.definitionVersion
      : 1,
  };
}

function declarationOverrides(current, defaults) {
  return Object.fromEntries(Object.keys(defaults).map((key) => {
    const declaration = current?.[key];
    return [key, declaration === undefined ? defaults[key] : declaration];
  }));
}

function governedSurfaceDeclarations(current, defaults) {
  return Object.fromEntries(Object.entries(defaults).map(([key, baseline]) => {
    const declaration = current?.[key];
    const enabled = typeof declaration === 'boolean'
      ? declaration
      : (typeof declaration?.enabled === 'boolean' ? declaration.enabled : baseline.enabled);
    return [key, {
      ...baseline,
      enabled,
      ...(key === 'standalone' && declaration?.route !== undefined
        ? { route: declaration.route }
        : {}),
    }];
  }));
}

/**
 * Adopt a school-authored legacy Domain into model v2. Existing fields and
 * collection routes stay intact; implicit powers do not. Recording remains on,
 * while every additional capability and surface is made explicit.
 */
export function governSchoolDomainConfig(domain) {
  const policyLibrary = domain?.capabilities?.policyLibrary?.enabled === true;
  const upgradingToCollectiveModel = (domain?.governance?.appliedModelVersion || 0) < 3;
  const capabilityDefaults = buildDomainCapabilities({
    recording: !policyLibrary,
    collective: !policyLibrary,
  });
  const capabilities = declarationOverrides(domain?.capabilities, capabilityDefaults);
  if (upgradingToCollectiveModel && !policyLibrary) {
    capabilities.collective = {
      enabled: true,
      aggregates: ['count', 'breakdown', 'trend', 'baseline'],
      audiences: ['staff', 'parent-summary'],
    };
  }
  const quickIssueExplicitlyHidden = Array.isArray(domain?.inputSurfaces)
    && domain.inputSurfaces.length === 0;
  const surfaceDefaults = buildDomainSurfaces({
    quickIssue: !policyLibrary && !quickIssueExplicitlyHidden,
    queue: !policyLibrary,
    records: !policyLibrary,
    rules: capabilities.rules?.enabled === true,
    classDisplay: Array.isArray(domain?.inputSurfaces)
      && domain.inputSurfaces.includes('class-display'),
    dashboard: capabilities.collective?.enabled === true,
    reports: capabilities.collective?.enabled === true,
    standalone: policyLibrary
      ? { enabled: true, route: capabilities.policyLibrary?.route || null }
      : false,
  });
  const surfaces = governedSurfaceDeclarations(domain?.surfaces, surfaceDefaults);
  if (upgradingToCollectiveModel && !policyLibrary) {
    surfaces.dashboard = {
      ...surfaceDefaults.dashboard,
      enabled: true,
    };
    surfaces.reports = {
      ...surfaceDefaults.reports,
      enabled: true,
    };
  }
  const storedConfig = clone(domain);
  delete storedConfig.governance;
  return {
    ...storedConfig,
    protected: false,
    capabilities,
    surfaces,
    reporting: {
      parentWeeklySummary: false,
      staffWeeklyBrief: false,
      ...(storedConfig.reporting || {}),
    },
  };
}

function operationalMetadata(stored) {
  return Object.fromEntries([
    'schoolId',
    'createdAt',
    'createdBy',
    'updatedAt',
  ].filter((key) => stored?.[key] !== undefined).map((key) => [key, stored[key]]));
}

function declaredBindings(stored, definition) {
  if (Object.keys(stored?.governance?.bindings || {}).length > 0) {
    return clone(stored.governance.bindings);
  }
  const bindings = {};
  for (const path of definition.runtime.immutableBindings || []) {
    const value = getPath(stored, path);
    if (value !== undefined) setPath(bindings, path, value);
  }
  return bindings;
}

function hasPersistedV4Configuration(stored) {
  return stored?.governance?.modelVersion === CENTRAL_DOMAINS_MODEL.version
    && stored?.governance?.prototypeId
    && isPlainConfiguration(stored?.governance?.configuration || stored?.configuration);
}

function isPlainConfiguration(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

// Older school forms stored Yes/No as `binary`; the canonical field type is
// `boolean`. Adapt the read projection before deriving/validating configuration.
// Keep the stored document, field IDs and recorded answers unchanged.
function normalizeStoredBooleanFields(stored) {
  const normalized = clone(stored);
  const normalizeField = (field) => {
    if (field?.type === 'binary') field.type = 'boolean';
  };
  normalizeField(normalized?.primary);
  for (const fields of [normalized?.fields, normalized?.followUp]) {
    if (Array.isArray(fields)) fields.forEach(normalizeField);
  }
  for (const configuration of [normalized?.configuration, normalized?.governance?.configuration]) {
    const additions = configuration?.event?.fieldAdditions;
    if (Array.isArray(additions)) additions.forEach(normalizeField);
  }
  return normalized;
}

export function resolveStoredGovernedDomain(stored, { assumeCurrent = false } = {}) {
  stored = normalizeStoredBooleanFields(stored);
  const central = centralDefinitionForStoredDomain(stored);
  const definition = domainDefinitionForStoredDomain(stored);
  const schoolAuthored = definition.prototypeId === GENERIC_RECORDING_PROTOTYPE_ID
    && definition.id !== GENERIC_RECORDING_PROTOTYPE_ID;
  if (central && !schoolAuthored) {
    const governance = stored?.governance || {};
    const installation = {
      domainId: stored.id,
      definitionId: central.id,
      appliedModelVersion: assumeCurrent
        ? CENTRAL_DOMAINS_MODEL.version
        : (Number.isInteger(governance.appliedModelVersion)
          ? governance.appliedModelVersion
          : 0),
      appliedDefinitionVersion: assumeCurrent
        ? central.definitionVersion
        : (Number.isInteger(governance.appliedDefinitionVersion)
          ? governance.appliedDefinitionVersion
          : 0),
      prototypeId: central.prototypeId,
      appliedPrototypeVersion: assumeCurrent
        ? central.prototypeVersion
        : (Number.isInteger(governance.appliedPrototypeVersion)
          ? governance.appliedPrototypeVersion
          : 0),
      definitionAuthority: governance.definitionAuthority
        || (central.id === GENERIC_RECORDING_PROTOTYPE_ID ? 'school' : 'central'),
      configurationRevision: Number.isInteger(governance.configurationRevision)
        ? governance.configurationRevision
        : 0,
      active: stored.active !== false,
      bindings: declaredBindings(stored, central),
      overrides: Object.keys(governance.overrides || {}).length > 0
        ? governance.overrides
        : pickSchoolOverrides(stored),
      configuration: clone(stored.configuration || governance.configuration || {}),
    };
    return {
      ...resolveEffectiveDomain({
        definition: central,
        installation,
        allowPendingMigration: true,
      }),
      ...operationalMetadata(stored),
    };
  }

  const config = governSchoolDomainConfig(stored);
  const genericPrototype = getCentralDomainDefinition(GENERIC_RECORDING_PROTOTYPE_ID);
  if (!Array.isArray(config.inputSurfaces)) {
    config.inputSurfaces = clone(genericPrototype.config.inputSurfaces || []);
  }
  const definitionVersion = definition.definitionVersion;
  const appliedModelVersion = assumeCurrent
    ? CENTRAL_DOMAINS_MODEL.version
    : (Number.isInteger(stored?.governance?.appliedModelVersion)
      ? stored.governance.appliedModelVersion
      : 0);
  const appliedDefinitionVersion = assumeCurrent
    ? definitionVersion
    : (stored?.governance?.appliedDefinitionVersion || 0);
  const appliedPrototypeVersion = assumeCurrent
    ? genericPrototype.prototypeVersion
    : (stored?.governance?.appliedPrototypeVersion || 0);
  const configuration = hasPersistedV4Configuration(stored)
    ? clone(stored.governance?.configuration || stored.configuration || {})
    : deriveDomainConfiguration(definition, config);
  const bindings = declaredBindings(stored, definition);
  return {
    ...resolveEffectiveDomain({
      definition,
      installation: {
        domainId: stored.id,
        definitionId: definition.id,
        appliedModelVersion,
        appliedDefinitionVersion,
        prototypeId: GENERIC_RECORDING_PROTOTYPE_ID,
        appliedPrototypeVersion,
        definitionAuthority: stored?.governance?.definitionAuthority || 'school',
        configurationRevision: Number.isInteger(stored?.governance?.configurationRevision)
          ? stored.governance.configurationRevision
          : 0,
        active: stored.active !== false,
        bindings,
        overrides: {},
        configuration,
      },
      allowPendingMigration: true,
    }),
    ...operationalMetadata(stored),
  };
}

/** Materialised shape written by the migration/operator for all runtimes. */
export function materializeGovernedDomain(stored) {
  return resolveStoredGovernedDomain(stored, { assumeCurrent: true });
}
